import React, { useCallback, useEffect, useState } from "react";
import { InputV2 } from "../../../components/redesign-components/InputV2/InputV2";
import { useDropzone } from "react-dropzone";
import { FiUploadCloud } from "react-icons/fi";
import { updateUserDetails } from "../api";
import { useMediaQuery } from "react-responsive";
import Swal from "sweetalert2";
import Divider from "../../../components/shared/Divider/Divider";

export const AccountDetailsChange = ({ picture: userPicture, name, email }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    repeatPassword: "",
  });

  const [passwordErrors, setPasswordErrors] = useState({
    isNotStrong: false,
    isNotSame: false,
  });

  const [picture, setPicture] = useState(null);

  const [fileState, setFileState] = useState(null);

  const onDrop = useCallback((files) => {
    setFileState(files[0]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 900px)" });

  useEffect(() => {
    setForm((prev) => ({ ...prev, name, email }));
    setPicture(userPicture);
  }, [name, email, userPicture]);

  useEffect(() => {
    if (fileState) {
      const objectUrl = URL.createObjectURL(fileState);
      setPicture(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [fileState]);

  const onSubmit = useCallback(async () => {
    if (form.password) {
      const sameCheck = form.password === form.repeatPassword;
      const strongCheck = RegExp(
        /^(?=.*\d)(?=.*[.!@#$%^&*])(?=.*[a-z]).{8,}$/
      ).test(form.password);

      if (!strongCheck) {
        setPasswordErrors((prev) => ({ ...prev, isNotStrong: true }));
      } else if (!sameCheck) {
        setPasswordErrors({ isNotStrong: false, isNotSame: true });
      } else {
        setPasswordErrors({ isNotSame: false, isNotStrong: false });
        const data = { userName: form.name, password: form.password, email };

        if (fileState) data.picture = fileState;
        await updateUserDetails(data);

        setForm((prev) => ({ ...prev, password: "", repeatPassword: "" }));

        Swal.fire({
          title: '<p style="font-size:70%;">Your changes have been saved!</p>',
          icon: "success",
          confirmButtonText: "Ok",
          allowOutsideClick: false,
          timer: 2000,
          customClass: {
            container: "my-swal",
          },
        });
      }
    } else {
      const data = { userName: form.name, email };

      if (fileState) data.picture = fileState;
      await updateUserDetails(data);

      Swal.fire({
        title: '<p style="font-size:70%;">Your changes have been saved!</p>',
        icon: "success",
        confirmButtonText: "Ok",
        allowOutsideClick: false,
        timer: 2000,
        customClass: {
          container: "my-swal",
        },
      });
    }
  }, [form, fileState, email]);

  const handleInput = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const getInitials = (name) => {
    const words = name.split(" ");
    let initials = "";

    for (let i = 0; i < words.length; i++) {
      initials += words[i].charAt(0);
    }
    initials = initials.toUpperCase();

    return initials;
  };

  const getDisabledState = () => {
    let state = true;
    if (picture) {
      state = false;
    }
    if (form.name !== name) {
      state = false;
    }
    if (
      form.repeatPassword.length > 0 &&
      form.repeatPassword === form.password
    ) {
      state = false;
    }
    return state;
  };

  return (
    <div className="p-6 rounded-xl border border-gray200 shadow-[0_1px_3px_0_rgba(14,24,41,0.10),_0_1px_2px_0_rgba(14,24,41,0.06)]">
      <div className="gap-4 flex flex-row justify-between w-full max-w-[1000px]">
        <div className="text-black font-bold text-sm text-nowrap">
          Profile picture
        </div>
        <div
          className="cursor-pointer flex flex-row gap-3 w-full max-w-[620px]"
          {...getRootProps()}
        >
          {/* Avatar */}
          <div
            className={
              "relative flex items-center justify-center rounded-full border border-4 border-white size-24 min-w-24 min-h-24 shadow-[0_1px_3px_0_rgba(14,24,41,0.10),_0_1px_2px_0_rgba(14,24,41,0.06)]"
            }
            style={{
              backgroundColor: "#bdbdbd",
            }}
          >
            {picture ? (
              <img
                src={picture}
                alt="Profile"
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              <p
                className={[
                  "text-white",
                  isTabletOrMobile ? "text-4xl" : "text-5xl",
                ].join(" ")}
              >
                {getInitials(name)}
              </p>
            )}
          </div>

          {!isTabletOrMobile && (
            <div className="rounded-xl border border-gray200 py-4 px-6 flex cursor-pointer flex-col items-center justify-center w-full">
              <input {...getInputProps()} />

              <div className="flex items-center justify-center rounded-full size-10 min-w-10 bg-gray50">
                <div className="flex items-center justify-center rounded-full size-8 min-w-8 bg-gray100">
                  <FiUploadCloud className="size-5 min-w-5 text-gray600" />
                </div>
              </div>
              <div className="text-gray600 text-sm">
                <span className="font-semibold text-primary700">
                  Click to upload
                </span>{" "}
                or drag and drop
              </div>
              <div className="text-gray600 text-xs">
                SVG, PNG, JPG or GIF (max. 800x400px)
              </div>
            </div>
          )}
        </div>
      </div>

      <Divider className="my-8" color="gray200" type="horizontal"/>

      <div className="flex flex-col gap-4">
        <div className="gap-4 flex flex-col lg:flex-row justify-between w-full max-w-[1000px]">
          <div className="text-black font-bold text-sm text-nowrap">Name</div>
          <div className="w-full max-w-[620px]">
            <InputV2 onChange={handleInput} name="name" value={form.name} />
          </div>
        </div>
        <div className="gap-4 flex flex-col lg:flex-row justify-between w-full max-w-[1000px]">
          <div className="text-black font-bold text-sm text-nowrap">Email</div>
          <div className="w-full max-w-[620px]">
            <InputV2
              disabled
              onChange={() => {}}
              name="email"
              value={form.email}
            />
          </div>
        </div>
      </div>

      <Divider className="my-8" color="gray200" type="horizontal"/>

      <div className="flex flex-col gap-4">
        <div className="gap-4 flex flex-col lg:flex-row justify-between w-full max-w-[1000px]">
          <div className="text-black font-bold text-sm text-nowrap">
            New password
          </div>
          <div className="w-full max-w-[620px]">
            <InputV2
              type="password"
              name="password"
              value={form.password}
              onChange={handleInput}
            />
            {passwordErrors.isNotStrong && (
              <p className="text-error700">Password is not strong enough</p>
            )}
            {form.password && (
              <div className="w-fit mt-1">
                <p>Password must contain:</p>
                <ul>
                  <li>• At least 8 characters</li>
                  <li>• At least one number</li>
                  <li>• A special character (e.g., !@#$%&*)</li>
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="gap-4 flex flex-col lg:flex-row justify-between w-full max-w-[1000px]">
          <div className="text-black font-bold text-sm text-nowrap">
            Repeat new password
          </div>
          <div className="w-full max-w-[620px]">
            <InputV2
              type="password"
              name="repeatPassword"
              value={form.repeatPassword}
              onChange={handleInput}
            />
            {form.repeatPassword && form.password !== form.repeatPassword && (
              <p className="text-error700">Password does not match</p>
            )}
            {passwordErrors.isNotSame && (
              <p className="text-error700">Password does not match</p>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={onSubmit}
        disabled={getDisabledState()}
        className="mt-8 w-fit min-h-11 h-11 rounded-full font-semibold text-sm btnPrimary500"
      >
        Save changes
      </button>
    </div>
  );
};
